import { siteConfig } from '@/config/site';
import type { ToolDetail } from '@/modules/directory/types';

/**
 * Structured-data (schema.org) builders for the AI-tools directory.
 *
 * These return plain JSON objects — they contain no React/server code. They're
 * rendered into the page HTML by the `<JsonLd />` component so crawlers see
 * them without executing JavaScript.
 */

const SCHEMA = 'https://schema.org';

function absUrl(path: string): string {
  return `${siteConfig.url}${path === '/' ? '' : path}`;
}

/** WebSite + SearchAction — for the home page (purely additive, no TDK change). */
export function websiteJsonLd(opts?: { dateModified?: string }) {
  const data: Record<string, unknown> = {
    '@context': SCHEMA,
    '@type': 'WebSite',
    name: siteConfig.name,
    alternateName: siteConfig.tagline,
    url: siteConfig.url,
    description: siteConfig.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${absUrl('/search')}?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
  // Freshness signal for AI engines — pass the newest launch's createdAt.
  if (opts?.dateModified) data.dateModified = opts.dateModified;
  return data;
}

/**
 * Organization — the brand entity for the home page. Anchors AIGCLIST in
 * AI knowledge graphs alongside the WebSite node (WebSite = the site,
 * Organization = who runs it).
 */
export function organizationJsonLd() {
  return {
    '@context': SCHEMA,
    ...organizationNode(),
    description: siteConfig.description,
    email: siteConfig.mail,
  };
}

/**
 * Build a valid Offer only when we know the price. For paid/freemium/contact
 * pricing we omit the Offer rather than emit a fabricated price — invalid
 * structured data is worse than none.
 */
function offerFor(pricing: string | undefined, website: string | undefined) {
  if (pricing === 'free') {
    const offer: Record<string, unknown> = {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    };
    if (website) offer.url = website;
    return offer;
  }
  return undefined;
}

/**
 * Google's app rich result requires `applicationCategory` to come from a fixed
 * enum (BusinessApplication, MultimediaApplication, …). Free-text values fail
 * validation and disqualify the rich result, so taxonomy names are mapped onto
 * the closest enum value with a safe fallback.
 */
const APPLICATION_CATEGORY_RULES: Array<[RegExp, string]> = [
  [/(design|illustrat|logo|\bui\b|\bux\b|font|brand|present|slide)/i, 'DesignApplication'],
  [/(develop|code|coding|program|\bapi\b|\bcli\b|\bide\b|software eng|database|sql)/i, 'DeveloperApplication'],
  [/(image|photo|picture|video|audio|music|\bart\b|anime|film|edit|media|voice|speech|\b3d\b|avatar|podcast)/i, 'MultimediaApplication'],
  [/(educat|learn|course|teach|study|tutor)/i, 'EducationalApplication'],
  [/(financ|account|payment|invoice|\btax\b|bookkeep|payroll|crypto|trading)/i, 'FinanceApplication'],
  [/(health|medical|fitness|therap|mental|clinic)/i, 'HealthApplication'],
  [/(game|gaming)/i, 'GameApplication'],
  [/(social|community|forum|dating)/i, 'SocialNetworkingApplication'],
  [/(security|password|\bvpn\b|encrypt|malware)/i, 'SecurityApplication'],
  [/(travel|flight|hotel|\bmap\b|itinerar)/i, 'TravelApplication'],
  [/(lifestyle|habit|recipe|food|journal|planner)/i, 'LifestyleApplication'],
  [/(entertain|movie|meme|fun)/i, 'EntertainmentApplication'],
];

const APPLICATION_CATEGORY_FALLBACK = 'BusinessApplication';

export function applicationCategoryFor(categories?: Array<{ name?: string | null }>): string {
  for (const category of categories ?? []) {
    const name = (category?.name ?? '').toLowerCase();
    if (!name) continue;
    for (const [pattern, value] of APPLICATION_CATEGORY_RULES) {
      if (pattern.test(name)) return value;
    }
  }
  return APPLICATION_CATEGORY_FALLBACK;
}

/**
 * Shared Organization entity — anchors the brand in AI knowledge graphs.
 * `sameAs` points at canonical profile URLs so engines can consolidate
 * identity (shortener/tracking URLs are deliberately excluded).
 */
function organizationNode() {
  return {
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    logo: absUrl(siteConfig.logo),
    sameAs: [siteConfig.links.github, siteConfig.links.linkedin],
  };
}

/** SoftwareApplication — for a tool detail page. */
export function softwareApplicationJsonLd(
  item: ToolDetail,
  opts?: { featureList?: string[]; dateModified?: string },
) {
  const category = item.categories?.[0]?.name;
  const org = organizationNode();
  const data: Record<string, unknown> = {
    '@context': SCHEMA,
    '@type': 'SoftwareApplication',
    name: item.name,
    url: item.website || absUrl(`/item/${item.slug}`),
    applicationCategory: applicationCategoryFor(item.categories),
    // CreativeWork provenance: who maintains this profile and when.
    author: org,
    publisher: org,
    datePublished: item.createdAt,
  };
  if (category) data.applicationSubCategory = category;
  if (item.description) data.description = item.description;
  if (item.icon) data.image = item.icon;
  if (item.platforms?.length) data.operatingSystem = item.platforms.join(', ');
  if (item.screenshots?.length) data.screenshot = item.screenshots.slice(0, 5);
  if (opts?.featureList?.length) data.featureList = opts.featureList.slice(0, 12);
  if (opts?.dateModified) data.dateModified = opts.dateModified;

  const offer = offerFor(item.pricing, item.website);
  if (offer) data.offers = offer;

  if (item.rating && item.ratingCount) {
    data.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: item.rating,
      ratingCount: item.ratingCount,
      bestRating: 5,
      worstRating: 0,
    };
  }
  return data;
}

/** BreadcrumbList — Home > [Category] > Item. */
export function breadcrumbJsonLd(crumbs: { name: string; path: string }[]) {
  return {
    '@context': SCHEMA,
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: absUrl(c.path),
    })),
  };
}

export function faqPageJsonLd(faqs: { question: string; answer: string }[]) {
  return {
    '@context': SCHEMA,
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/** CollectionPage — for category / collection listing pages. */
export function collectionPageJsonLd(opts: {
  name: string;
  description?: string;
  path: string;
}) {
  const data: Record<string, unknown> = {
    '@context': SCHEMA,
    '@type': 'CollectionPage',
    name: opts.name,
    url: absUrl(opts.path),
    isPartOf: { '@type': 'WebSite', name: siteConfig.name, url: siteConfig.url },
  };
  if (opts.description) data.description = opts.description;
  return data;
}

/**
 * ItemList — for ranked pSEO pages ("X alternatives", "best X for Y").
 * Items must be on-page links (internal /item/{slug} paths), not outbound.
 */
export function itemListJsonLd(opts: {
  name: string;
  description?: string;
  path: string;
  items: { name: string; path: string }[];
}) {
  const data: Record<string, unknown> = {
    '@context': SCHEMA,
    '@type': 'ItemList',
    name: opts.name,
    url: absUrl(opts.path),
    numberOfItems: opts.items.length,
    itemListElement: opts.items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: absUrl(item.path),
    })),
  };
  if (opts.description) data.description = opts.description;
  return data;
}

/** Article — for blog post detail pages. */
export function articleJsonLd(post: {
  title: string;
  description?: string;
  slug: string;
  image?: string;
  createdAt?: string;
  authorName?: string;
}) {
  const data: Record<string, unknown> = {
    '@context': SCHEMA,
    '@type': 'Article',
    headline: post.title,
    url: absUrl(`/blog/${post.slug}`),
    mainEntityOfPage: { '@type': 'WebPage', '@id': absUrl(`/blog/${post.slug}`) },
    publisher: organizationNode(),
  };
  if (post.description) data.description = post.description;
  if (post.image) data.image = post.image;
  if (post.createdAt) data.datePublished = post.createdAt;
  if (post.authorName) {
    data.author = { '@type': 'Person', name: post.authorName };
  }
  return data;
}
